"use client";

import * as React from "react";
import { Cell, Pie, PieChart } from "recharts";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	seriesColor,
} from "./chart";
import {
	ChartCenterLabel,
	useTotal,
	type PolarChartProps,
} from "./chart-polar";

export type ChartPieProps = PolarChartProps & {
	/**
	 * - `default` — a solid pie.
	 * - `donut` — a pie with a hollow centre.
	 * - `donut-text` — a donut with a total (or `centerLabel`) in the middle.
	 * - `legend` — a solid pie with a legend.
	 */
	variant?: "default" | "donut" | "donut-text" | "legend";
	/**
	 * Radius of the hollow centre on the donut variants, as a percentage of the
	 * chart radius or an absolute pixel value. Defaults to `"60%"`; a smaller
	 * value leaves a thicker ring. Ignored by the solid variants.
	 */
	innerRadius?: number | string;
	/** Gap between slices, in degrees. Defaults to `0`, so slices touch. */
	paddingAngle?: number;
	/**
	 * Sweeps the slices in on mount. Turn off when the chart is rasterized right
	 * after it renders, which would otherwise capture it part-drawn.
	 */
	isAnimationActive?: boolean;
	/**
	 * Replaces the whole tooltip row (swatch, label and value) with custom
	 * content. Passed straight to `ChartTooltipContent`'s `formatter`, so it
	 * receives Recharts' `(value, name, item, index, payload)`; the hovered
	 * datum is `item.payload`. Omit it to keep the default row, which
	 * `valueFormatter` formats.
	 */
	tooltipFormatter?: React.ComponentProps<
		typeof ChartTooltipContent
	>["formatter"];
};

/**
 * Pie / donut chart built on the shared `ChartContainer` primitives.
 * Palette-agnostic: each slice colour is read from `config` keyed by the slice's
 * category value, as `var(--color-<category>)`.
 */
export function ChartPie({
	data,
	config,
	dataKey,
	nameKey,
	variant = "default",
	className,
	valueFormatter,
	hideTooltip = false,
	centerLabel,
	centerSublabel,
	innerRadius = "60%",
	paddingAngle = 0,
	isAnimationActive = true,
	tooltipFormatter,
}: ChartPieProps) {
	const isDonut = variant === "donut" || variant === "donut-text";
	const withCenterText = variant === "donut-text";
	const withLegend = variant === "legend";
	const total = useTotal(data, dataKey);
	const keyedData = withOccurrenceKeys(data, (datum) => String(datum[nameKey]));

	return (
		<ChartContainer config={config} className={className}>
			<PieChart>
				{!hideTooltip && (
					<ChartTooltip
						content={
							<ChartTooltipContent
								hideLabel
								valueFormatter={valueFormatter}
								formatter={tooltipFormatter}
								nameKey={nameKey}
							/>
						}
					/>
				)}
				{withLegend && (
					<ChartLegend content={<ChartLegendContent nameKey={nameKey} />} />
				)}
				<Pie
					data={data}
					dataKey={dataKey}
					nameKey={nameKey}
					innerRadius={isDonut ? innerRadius : 0}
					paddingAngle={paddingAngle}
					strokeWidth={2}
					isAnimationActive={isAnimationActive}
				>
					{keyedData.map(({ item: datum, key: datumKey }) => (
						<Cell key={datumKey} fill={seriesColor(String(datum[nameKey]))} />
					))}
					{withCenterText && (
						<ChartCenterLabel
							label={centerLabel ?? total.toLocaleString()}
							sublabel={centerSublabel}
						/>
					)}
				</Pie>
			</PieChart>
		</ChartContainer>
	);
}
